import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import { api } from '../lib/api'
import '../styles/ui.css'
import '../styles/boss-reports.css'

// ── API types (mirror BossReportEndpoints.cs) ─────────────────────────

type BossShortcut = { key: string; label: string; description: string | null; sortOrder: number }
type BossShortcutGroup = { key: string; label: string; sortOrder: number; shortcuts: BossShortcut[] }
type BossCatalogue = { groups: BossShortcutGroup[] }

// ── Page ──────────────────────────────────────────────────────────────

/**
 * BOSS Reports — the legacy WinForms pivot-grid reporting tool, ported.
 *
 * Bare-bones scaffold: a shortcut panel bound to the catalogue endpoint, a pre-filter strip and
 * a pivot-grid area. The grid component, report definitions and data endpoint follow the design
 * spec in docs/BossReports.md.
 */
export default function BossReportsPage() {
    const token = useAuthStore(s => s.accessToken)
    const [selected, setSelected] = useState<BossShortcut | null>(null)

    const catalogue = useQuery<BossCatalogue>({
        queryKey: ['boss-reports-catalogue'],
        queryFn: () => api('/api/boss-reports/catalogue', token),
        enabled: !!token,
        staleTime: 5 * 60_000,
    })

    const groups = (catalogue.data?.groups ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder)

    return (
        <div className="page boss-page">
            <div className="page-header">
                <h1>🧮 BOSS Reports</h1>
                <p>Pivot-grid reporting ported from the legacy BOSS Reporting desktop tool.</p>
            </div>

            <div className="boss-layout">
                {/* Shortcut panel — groups of report shortcuts, as in the legacy tree */}
                <aside className="boss-shortcuts" aria-label="Report shortcuts">
                    <div className="boss-panel-title">Report shortcuts</div>
                    {catalogue.isLoading && <div className="boss-muted">Loading…</div>}
                    {catalogue.error && (
                        <div className="boss-muted">The report catalogue could not be loaded.</div>
                    )}
                    {catalogue.data && groups.length === 0 && (
                        <div className="boss-muted">No report shortcuts have been ported yet.</div>
                    )}
                    {groups.map(g => (
                        <div key={g.key} className="boss-group">
                            <div className="boss-group-label">{g.label}</div>
                            {g.shortcuts.slice().sort((a, b) => a.sortOrder - b.sortOrder).map(s => (
                                <button
                                    key={s.key}
                                    type="button"
                                    className={`boss-shortcut${selected?.key === s.key ? ' active' : ''}`}
                                    title={s.description ?? undefined}
                                    onClick={() => setSelected(s)}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    ))}
                </aside>

                {/* Report area — pre-filters above, pivot grid below */}
                <section className="boss-report">
                    <div className="boss-prefilters" aria-label="Pre-filters">
                        <span className="boss-muted">
                            {selected
                                ? `Pre-filters for “${selected.label}” will appear here.`
                                : 'Pick a report shortcut to set its pre-filters.'}
                        </span>
                    </div>

                    <div className="boss-grid">
                        <div className="empty-state">
                            <div className="icon">🧮</div>
                            <h3>{selected ? selected.label : 'No report selected'}</h3>
                            <p>The Perspective pivot grid will render here once the first report definition is ported (see docs/BossReports.md).</p>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    )
}
